import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import apiClient from '@/lib/api/apiClient';
import {
  CreateInvestmentIssueRequestBody,
  CreateInvestmentIssueResponse,
  GetInvestmentIssueStatsResponse,
  IssueStatus,
  ListInvestmentIssuesResponse,
  UpdateInvestmentIssueRequestBody,
  UpdateInvestmentIssueResponse,
} from '@inversor-hub/types';

export const INVESTMENT_ISSUES_KEY = 'investment-issues';
export const INVESTMENT_ISSUE_STATS_KEY = 'investment-issue-stats';

export function useListInvestmentIssues(investmentId?: string, status?: IssueStatus) {
  return useQuery({
    queryKey: [INVESTMENT_ISSUES_KEY, investmentId, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);

      const queryString = params.toString();
      const url = `/investments/${investmentId}/issues${queryString ? `?${queryString}` : ''}`;

      return apiClient.get<ListInvestmentIssuesResponse>(url);
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
    placeholderData: keepPreviousData,
  });
}

export function useGetInvestmentIssueStats(investmentId?: string) {
  return useQuery({
    queryKey: [INVESTMENT_ISSUE_STATS_KEY, investmentId],
    queryFn: async () =>
      apiClient.get<GetInvestmentIssueStatsResponse>(`/investments/${investmentId}/issues/stats`),
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useCreateInvestmentIssue(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvestmentIssueRequestBody) => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<CreateInvestmentIssueResponse>,
        CreateInvestmentIssueRequestBody
      >(`/investments/${investmentId}/issues`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUES_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUE_STATS_KEY, investmentId] });
    },
  });
}

export function useUpdateInvestmentIssue(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      issueId,
      data,
    }: {
      issueId: string;
      data: UpdateInvestmentIssueRequestBody;
    }) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<UpdateInvestmentIssueResponse>,
        UpdateInvestmentIssueRequestBody
      >(`/investments/${investmentId}/issues/${issueId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUES_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUE_STATS_KEY, investmentId] });
    },
  });
}

export function useDeleteInvestmentIssue(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (issueId: string) => {
      await apiClient.delete(`/investments/${investmentId}/issues/${issueId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUES_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUE_STATS_KEY, investmentId] });
    },
  });
}

export function useUploadInvestmentIssueDocument(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ issueId, file }: { issueId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/investments/${investmentId}/issues/${issueId}/document`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUES_KEY, investmentId] });
    },
  });
}

export function useDeleteInvestmentIssueDocument(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (issueId: string) => {
      await apiClient.delete(`/investments/${investmentId}/issues/${issueId}/document`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_ISSUES_KEY, investmentId] });
    },
  });
}
